/**
 * Apt / Ste / Flr as a choice rather than free text.
 *
 * USCIS address blocks offer exactly three boxes — Apartment, Suite, Floor —
 * and a text field for the number. Our intake asked for all of it as one
 * free-text line, so people typed what they say out loud ("Ap 102", "102",
 * "Apt 4B") and the form filler had to guess which box to tick. A spelling it
 * did not recognise reached the printed form as "Ap 102" beside a ticked
 * Apartment box.
 *
 * Asking for the type directly removes the guess. The value is still stored as
 * ONE string under the same JSON key (`address2` / `apt`), just written in the
 * canonical spelling — so every answer already in the database keeps working,
 * nothing downstream changes shape, and no migration is needed.
 *
 * The vocabulary matches `Preparer::$unit_type` and the microservice's
 * `_parse_unit`, so the three surfaces agree on what "ste" means.
 */

export const UNIT_TYPES = [
    { value: 'apt', label: 'Apt' },
    { value: 'ste', label: 'Ste' },
    { value: 'flr', label: 'Flr' },
];

/** Canonical prefix written back out, per type. */
const LABEL = { apt: 'Apt', ste: 'Ste', flr: 'Flr' };

/**
 * Spellings seen in real answers, mapped to the box they mean.
 * Longest first so "apartment" is not read as "apt" followed by "ment".
 */
const PREFIXES = [
    ['apartamento', 'apt'],
    ['apartment', 'apt'],
    ['depto', 'apt'],
    ['dept', 'apt'],
    ['apto', 'apt'],
    ['unit', 'apt'],
    ['room', 'apt'],
    ['apt', 'apt'],
    ['rm', 'apt'],
    ['ap', 'apt'],
    ['suite', 'ste'],
    ['ste', 'ste'],
    ['floor', 'flr'],
    ['piso', 'flr'],
    ['flr', 'flr'],
    ['fl', 'flr'],
];

// A prefix only counts when what follows is a separator or a digit: "Apt4B"
// and "Ste. 200" are units, "Flat 3" and "Apartado 12" are not.
const PATTERN = new RegExp(
    `^(${PREFIXES.map(([word]) => word).join('|')})(?=[\\s#.\\d])\\.?\\s*#?\\s*(.+)$`,
    'i',
);

/**
 * Split a stored unit line into the picker's two values.
 *
 * An answer that never named a type ("102") comes back with an empty type: the
 * person should choose, rather than us deciding on their behalf.
 *
 * @param {string|null|undefined} value
 * @returns {{type: string, number: string}}
 */
export function parseUnit(value) {
    const raw = String(value ?? '').trim();

    if (!raw) {
        return { type: '', number: '' };
    }

    const match = raw.match(PATTERN);

    if (match) {
        const word = match[1].toLowerCase();
        const found = PREFIXES.find(([prefix]) => prefix === word);

        return { type: found ? found[1] : '', number: match[2].trim() };
    }

    // A bare "#5" names a unit without saying which kind.
    const hashed = raw.match(/^#\s*(.+)$/);

    return { type: '', number: hashed ? hashed[1].trim() : raw };
}

/**
 * Compose the two picker values back into the single stored string.
 *
 * A type with no number is not an address line, so it stores as empty rather
 * than a dangling "Apt".
 *
 * @param {string} type  one of UNIT_TYPES' values, or ''
 * @param {string} number
 * @returns {string}
 */
export function formatUnit(type, number) {
    const unit = String(number ?? '').trim();

    if (!unit) {
        return '';
    }

    const label = LABEL[String(type ?? '').toLowerCase()];

    return label ? `${label} ${unit}` : unit;
}

const read = (obj, path) => path.split('.').reduce((o, k) => (o == null ? o : o[k]), obj);

const write = (obj, path, value) => {
    const keys = path.split('.');
    const last = keys.pop();
    const target = keys.reduce((o, k) => o[k], obj);
    target[last] = value;
};

/**
 * Alpine mixin backing the Apt/Ste/Flr picker.
 *
 * Spread into a component and call `watchUnit()` from `init()`:
 *
 *     ...unitField('parsedAddress.address2'),
 *     init() { …; this.watchUnit(); }
 *
 * The picker is real state rather than something derived from the stored
 * string, because choosing a type before typing a number would otherwise blank
 * the select — an empty unit stores as an empty line, by design.
 *
 * Keeping it in step is a watcher rather than a hydrate call at every
 * assignment site: these components rewrite the address from a Places result,
 * a saved entry, a reset and a legacy-format parse, and one missed site is a
 * picker showing the previous address's unit.
 *
 * @param {string} path    dotted path to the stored unit string
 * @param {string|null} persist method to call after writing it, or null when
 *   the component commits later (a draft entry not yet added to a list)
 */
export function unitField(path, persist = 'updateValue') {
    return {
        unitType: '',
        unitNumber: '',

        /** Read the picker back out of whatever the stored string now holds. */
        hydrateUnit() {
            const { type, number } = parseUnit(read(this, path));
            this.unitType = type;
            this.unitNumber = number;
        },

        /** Write the picker into the stored string and persist. */
        syncUnit() {
            write(this, path, formatUnit(this.unitType, this.unitNumber));
            if (persist && typeof this[persist] === 'function') {
                this[persist]();
            }
        },

        watchUnit() {
            this.hydrateUnit();
            this.$watch(path, (value) => {
                // Our own write — leave the picker alone, so a chosen type
                // survives the moment before a number is typed.
                if (value === formatUnit(this.unitType, this.unitNumber)) {
                    return;
                }
                this.hydrateUnit();
            });
        },
    };
}
